import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user, token } = useAuth();

  const fetchCart = useCallback(async () => {
    if (!token) {
      // Load from localStorage for guests
      const localCart = localStorage.getItem('cart');
      if (localCart) {
        setCartItems(JSON.parse(localCart));
      }
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API}/cart`);
      setCartItems(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const syncCartToServer = async (items) => {
    if (!token) {
      localStorage.setItem('cart', JSON.stringify(items));
      return;
    }

    try {
      const cartData = items.map(item => ({
        product_id: item.product_id || item.product?.id,
        quantity: item.quantity
      }));
      await axios.post(`${API}/cart`, { items: cartData });
    } catch (error) {
      console.error('Failed to sync cart:', error);
    }
  };

  const addToCart = async (product, quantity = 1) => {
    const existingIndex = cartItems.findIndex(
      item => (item.product_id || item.product?.id) === product.id
    );

    let newItems;
    if (existingIndex >= 0) {
      newItems = cartItems.map((item, index) =>
        index === existingIndex
          ? { ...item, quantity: item.quantity + quantity }
          : item
      );
    } else {
      newItems = [...cartItems, { product_id: product.id, quantity, product }];
    }

    setCartItems(newItems);
    await syncCartToServer(newItems);
  };

  const updateQuantity = async (productId, quantity) => {
    if (quantity <= 0) {
      return removeFromCart(productId);
    }

    const newItems = cartItems.map(item =>
      (item.product_id || item.product?.id) === productId
        ? { ...item, quantity }
        : item
    );

    setCartItems(newItems);
    await syncCartToServer(newItems);
  };

  const removeFromCart = async (productId) => {
    const newItems = cartItems.filter(
      item => (item.product_id || item.product?.id) !== productId
    );
    setCartItems(newItems);
    await syncCartToServer(newItems);
  };

  const clearCart = async () => {
    setCartItems([]);
    if (token) {
      try {
        await axios.delete(`${API}/cart`);
      } catch (error) {
        console.error('Failed to clear cart:', error);
      }
    } else {
      localStorage.removeItem('cart');
    }
  };

  const cartTotal = cartItems.reduce((total, item) => {
    const price = item.product?.price || 0;
    return total + price * item.quantity;
  }, 0);

  const cartCount = cartItems.reduce((count, item) => count + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      cartItems,
      cartCount,
      cartTotal,
      loading,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      fetchCart
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
